import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { PositionFilterScreenProps } from 'home/stack/types'
import { cloneDeep, isEqual } from 'lodash'
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useFiltersContext } from 'schedule/filters/context'
import { MultiSelectSettingView } from 'settings/screens/types/multi_select/view'
import { SettingValue } from 'settings/types'
import Spinner from 'shared/components/spinner'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'
import { useRecentStoresContext } from 'stores/context'
import { useStore } from 'stores/queries/details'

export function PositionFilterScreen({
  route,
  navigation,
}: PositionFilterScreenProps) {
  const { theme } = useTheme()
  const { activeStoreId } = useRecentStoresContext()
  const storeQuery = useStore(activeStoreId)
  const [loading, setLoading] = useState<boolean>(false)

  const {
    currentFilters,
    setCurrentFiltersWithToggle,
    setCurrentSelectedPresetIndex,
  } = useFiltersContext()
  const positionFilter = currentFilters['positions']

  const [selectedIds, setSelectedIds] = useState<readonly string[]>(
    positionFilter.selectedIds
  )

  const [positions, setPositions] = useState<SettingValue[]>(
    positionFilter.values
  )

  const setPositionFilter = useCallback(() => {
    const newPositionFilter = {
      ...positionFilter,
      values: positions,
      selectedIds,
    }
    const newFilters = cloneDeep(currentFilters)
    if (
      !isEqual(
        newFilters['positions'].selectedIds,
        newPositionFilter.selectedIds
      )
    ) {
      newFilters['positions'] = newPositionFilter
      setCurrentFiltersWithToggle(newFilters)
      setCurrentSelectedPresetIndex(undefined)
    }
  }, [
    currentFilters,
    positionFilter,
    positions,
    selectedIds,
    setCurrentFiltersWithToggle,
    setCurrentSelectedPresetIndex,
  ])

  useLayoutEffect(
    () =>
      showHeaderButton({
        navigation,
        theme,
        title: t('done'),
        onPress: async () => {
          setLoading(true)
          setPositionFilter()
          navigation.goBack()
        },
      }),
    [navigation, setPositionFilter, theme]
  )

  useEffect(() => {
    if (storeQuery.data?.positions) {
      const multiSelectPositions = storeQuery.data?.positions?.map(position => {
        return { ...position, locked: false }
      })

      setPositions(multiSelectPositions)
    }
  }, [storeQuery.data?.positions])

  return (
    <StatusBoundary
      isLoading={storeQuery.isLoading}
      error={storeQuery.error}
    >
      {loading ? (
        <Spinner />
      ) : (
        <MultiSelectSettingView
          selectedIds={selectedIds as string[]}
          values={positions}
          onSelectValue={setSelectedIds}
        />
      )}
    </StatusBoundary>
  )
}
